import { useMsal } from '@azure/msal-react'
import { createContext, useContext, useEffect, useState } from 'react'
import { fetchUnreadCount } from '../data/notification.service'
import { useSessionInfo } from './SessionContext'

interface NotificationContextType {
  unreadCount: number
  refreshUnreadCount: () => void
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
)

export const useNotifications = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider',
    )
  }
  return context
}

interface NotificationProviderProps {
  children: React.ReactNode
}

export const NotificationProvider = ({
  children,
}: NotificationProviderProps) => {
  const { instance } = useMsal()
  const { session } = useSessionInfo()
  const [unreadCount, setUnreadCount] = useState(0)

  const refreshUnreadCount = async () => {
    try {
      // Check if instance is initialized and session is available
      if (!instance || !instance.getActiveAccount() || !session) {
        console.log(
          'MSAL instance or session not ready, skipping unread count fetch',
        )
        return
      }

      const count = await fetchUnreadCount(instance, session.organizationId)
      setUnreadCount(count)
    } catch (error) {
      console.error('Error fetching unread count:', error)
    }
  }

  useEffect(() => {
    // Load initial count immediately
    const loadInitialCount = async () => {
      await refreshUnreadCount()
    }

    loadInitialCount()

    // Refresh count every 30 seconds
    const interval = setInterval(refreshUnreadCount, 60000)
    return () => clearInterval(interval)
  }, [instance, session])

  return (
    <NotificationContext.Provider value={{ unreadCount, refreshUnreadCount }}>
      {children}
    </NotificationContext.Provider>
  )
}
